import type { ServiceDetail } from "./types";

export const INTELLIGENZA_ARTIFICIALE: ServiceDetail = {
  slug: "intelligenza-artificiale",
  category: "innovazione",
  name: "Intelligenza Artificiale",
  hero: {
    eyebrow: "Intelligenza Artificiale",
    title: "AI applicata al business, non slide motivazionali.",
    subtitle:
      "Parti da una valutazione tecnica AI del tuo processo: analizziamo un caso d'uso concreto — automazione documentale, AI su CRM/gestionale, estrazione dati, assistente amministrativo — e ti diciamo se e come conviene automatizzarlo, con quali strumenti e con quale approccio. Sede a Roma, certificati ISO 9001:2015.",
    bullets: [
      "Valutazione tecnica AI del tuo processo come primo passo",
      "Casi d'uso concreti per PMI, non demo da LinkedIn",
      "Compliance GDPR e DPA firmato con tutti i fornitori",
      "PoC e integrazione con i tuoi dati quando ha senso",
    ],
  },
  problem: [
    "L'intelligenza artificiale è ovunque a parole ma quasi nessuna PMI italiana la usa davvero. Il founder vede demo virali su LinkedIn, prova ChatGPT in incognito e poi non sa da dove iniziare nel suo contesto reale, con i suoi dati e i suoi processi.",
    "C'è la percezione che l'AI \"vera\" costi centinaia di migliaia di euro e richieda team di data scientist, mentre in realtà la maggior parte dei use case PMI si risolve con orchestration di LLM esistenti (GPT-4o, Gemini, Claude) e un'integrazione pulita con i dati aziendali.",
    "A questo si aggiungono i timori legittimi su GDPR e privacy dei dati: \"se carico i contratti dei miei clienti su un LLM, finiscono nel training del modello?\". E la paralisi di scelta tra mille strumenti — OpenAI, Gemini, Claude, modelli open-source self-hosted — senza una bussola tecnica indipendente.",
  ],
  approach: [
    "Valutazione tecnica AI (punto di partenza): analizziamo con te un processo concreto — automazione documentale, AI applicata a CRM/gestionale, estrazione dati da documenti, assistente amministrativo — e valutiamo in modo indipendente se l'AI è la scelta giusta, con quali strumenti e con quale approccio. È il modo più onesto per iniziare senza investimenti al buio.",
    "Discovery use case (1 settimana): workshop con il team operativo per mappare i processi che oggi consumano più ore o producono più errori. Output: shortlist di 3-5 use case AI con stima di ore risparmiate al mese, ROI atteso e fattibilità tecnica.",
    "PoC su un processo (3 settimane): scegliamo insieme l'use case con miglior rapporto valore/sforzo e costruiamo un proof of concept funzionante, integrato con un campione reale dei tuoi dati. Lo testi tu, non noi.",
    "Integrazione in produzione (4-8 settimane): se il PoC convince, lo portiamo in produzione integrato con CRM, ERP, gestionale o intranet. Tracciamento dei prompt, monitoring dei costi token, fallback se il modello non risponde bene.",
    "Training team + governance (ongoing): formazione operativa al team che userà lo strumento ogni giorno, scrittura di prompt library e policy interna sull'uso dell'AI (cosa si può dare in pasto al modello, cosa no), revisione mensile delle performance.",
  ],
  deliverables: [
    {
      title: "Valutazione tecnica AI del processo",
      detail:
        "Analisi indipendente di un caso d'uso concreto (automazione documentale, AI su CRM/gestionale, estrazione dati, assistente amministrativo): se conviene automatizzarlo, con quali strumenti e con quale approccio. È il primo passo consigliato.",
    },
    {
      title: "Mappa use case con priorità e ROI atteso",
      detail:
        "Shortlist di 3-5 use case AI candidati per la tua azienda, con stima di ore risparmiate al mese, ROI atteso e fattibilità tecnica.",
    },
    {
      title: "PoC funzionante in 3 settimane",
      detail:
        "Proof of concept del primo use case (chatbot, lead scoring, document automation, generazione copy) integrato con un campione reale dei tuoi dati.",
    },
    {
      title: "Integrazione nel CRM / ERP",
      detail:
        "Sviluppo dell'integrazione in produzione con CRM, ERP, gestionale o intranet aziendale, con monitoring costi token e fallback affidabili.",
    },
    {
      title: "Policy GDPR e data handling",
      detail:
        "DPA firmato con il fornitore LLM, classificazione dati ammessi, anonimizzazione automatica dove necessario e log degli accessi conforme a GDPR.",
    },
    {
      title: "Training del team operativo",
      detail:
        "Sessioni pratiche per usare il nuovo strumento ogni giorno, prompt library aziendale, video tutorial registrati e Q&A periodici.",
    },
    {
      title: "Governance del modello",
      detail:
        "Revisione mensile delle performance, monitoraggio costi token, log delle interazioni significative e roadmap per i use case successivi.",
    },
  ],
  process: [
    {
      step: "01",
      title: "Settimana 1 — Discovery",
      text: "AB MARK conduce workshop di mezza giornata con team operativo + founder e produce mappa use case con ROI atteso. Tu fornisci accesso a processi e dati di esempio. Output: shortlist condivisa.",
    },
    {
      step: "02",
      title: "Settimana 2-4 — PoC",
      text: "AB MARK sviluppa il proof of concept dell'use case scelto, integrato con un campione reale di dati. Tu lo testi con il team operativo e dai feedback prima di decidere se andare in produzione.",
    },
    {
      step: "03",
      title: "Settimana 5-12 — Integrazione",
      text: "AB MARK porta lo strumento in produzione, integrato con CRM/ERP/gestionale, con monitoring costi e fallback. Tu approvi gli ambienti di test e validi i casi limite con utenti reali.",
    },
    {
      step: "04",
      title: "Ongoing — Training & governance",
      text: "AB MARK forma il team, mantiene prompt library e policy aggiornate. Tu partecipi alla revisione mensile per validare risultati e prioritizzare i use case successivi.",
    },
  ],
  faq: [
    {
      q: "Quali modelli AI utilizzate?",
      a: "Lo standard è OpenAI (GPT-4o, GPT-4o mini, o-series) per generazione e ragionamento e Google Gemini (1.5 Pro, 2.0 Flash) per task multimodali e contesti lunghi. Per esigenze specifiche valutiamo anche Anthropic Claude e modelli open-source self-hosted (Llama, Mistral). La scelta è sempre guidata dal use case, non dall'hype del momento.",
    },
    {
      q: "Quanto costano i token e come tenete sotto controllo la spesa?",
      a: "Per la maggior parte dei use case PMI il costo token mensile è sotto i 200 € (es. chatbot interno con 1000 conversazioni/mese su GPT-4o mini). Per use case ad alta volume implementiamo caching delle risposte ricorrenti, scelta del modello giusto per ogni richiesta e dashboard di monitoraggio costi in tempo reale con alert.",
    },
    {
      q: "I miei dati restano riservati? Come gestite il GDPR?",
      a: "Lavoriamo solo con fornitori che garantiscono per contratto che i dati inviati non vengono usati per addestrare i modelli (OpenAI Enterprise/API, Azure OpenAI, Google Vertex AI con region UE, modelli self-hosted). Tutto è coperto da DPA GDPR firmato e i dati restano in datacenter UE. Per dati molto sensibili proponiamo anonimizzazione automatica prima dell'invio.",
    },
    {
      q: "Esistono alternative on-premise per dati ultra-sensibili?",
      a: "Sì. Per settori regolati (sanitario, legale, finanziario, difesa) o aziende con vincoli interni stringenti, deployamo modelli open-source self-hosted (Llama 3, Mistral, Qwen) su server dedicati in datacenter italiani o presso il vostro CED. Le performance sono inferiori ai modelli proprietari ma la sovranità del dato è totale.",
    },
    {
      q: "Avete un caso d'uso di riferimento da poter studiare?",
      a: "Sì, il nostro caso di punta è ERP Conversazionale per microimprese (vedi /erp-conversazionale): un assistente WhatsApp basato su GPT-4o che gestisce ordini, magazzino, clienti e dipendenti per attività con 1-9 dipendenti. Mostra concretamente come orchestriamo LLM, dati strutturati e canali esistenti (WhatsApp) in uno strumento operativo quotidiano.",
    },
    {
      q: "Quale ROI è ragionevole aspettarsi?",
      a: "Il ROI dipende dal use case ma su progetti tipici PMI vediamo break-even tra il mese 3 e il mese 6 dopo il go-live, considerando ore liberate del team operativo e riduzione errori. Use case ad alto volume (gestione ticket, document automation, lead qualification) tendono a essere più rapidi; quelli editoriali o creativi più lunghi e qualitativi.",
    },
  ],
  seo: {
    title: "Consulenza Intelligenza Artificiale PMI | AI Roma | AB MARK",
    description:
      "Consulenza intelligenza artificiale per PMI: AI per aziende italiane, automazione AI dei processi business, PoC in 3 settimane, integrazione GDPR e training.",
  },
};
